/**
 * Storefront mirror of the plan catalog.
 *
 * The server is the authority on what an order costs — see
 * `backend/src/domain/plans.ts`. This copy exists so the pricing cards and the
 * course picker can show a running total without a round trip; checkout sends
 * the figure it displayed and the server rejects the order outright if the two
 * disagree, so a drift here shows up as a blocked checkout rather than a wrong
 * charge. Keep the numbers below in step with that file.
 *
 * Ids here are the *storefront* ones. `PLAN_TO_BACKEND` in
 * `src/pages/PaymentRequest.tsx` maps them on the way out.
 */

/** Added per selected leaflet whose companion video is included. */
export const VIDEO_ADDON_PER_COURSE = 3;

/** Added once on the unlimited plan to include every companion video. */
export const VIDEO_ADDON_ALL_COURSES = 20;

/**
 * Storefront id of the order that buys a companion video on its own, for a
 * leaflet the member already owns. Same id on the backend.
 */
export const VIDEO_ADDON_PLAN_ID = "video";

/** What the video-only order is called on the payment page and in admin. */
export const VIDEO_ADDON_PLAN_NAME = "Companion Video";

export interface PlanTerms {
  /** Price in USD with no video add-ons. */
  basePrice: number;
  /** How many leaflets the plan lets you pick. Omitted means unlimited. */
  maxCourses?: number;
  /** True when video is one flat add-on covering everything. */
  videoIsAllOrNothing: boolean;
  /** True when the order sells only companion videos for leaflets already owned. */
  videoOnly?: boolean;
}

export const PLAN_TERMS: Record<string, PlanTerms> = {
  starter: { basePrice: 12, maxCourses: 1, videoIsAllOrNothing: false },
  pro:     { basePrice: 30, maxCourses: 3, videoIsAllOrNothing: false },
  elite:   { basePrice: 50, videoIsAllOrNothing: true },
  video:   { basePrice: 0, videoIsAllOrNothing: false, videoOnly: true },
};

export interface OrderSelection {
  planId: string;
  /** Ids bought with the companion video. Ignored on all-or-nothing plans. */
  videoCourseIds?: string[];
  /** Unlimited plan only. */
  allVideosIncluded?: boolean;
}

/** What the order costs in USD, or the base price for an unknown plan. */
export function computePlanPrice({
  planId,
  videoCourseIds,
  allVideosIncluded,
}: OrderSelection): number {
  const terms = PLAN_TERMS[planId];
  if (!terms) return 0;
  if (terms.videoIsAllOrNothing) {
    return terms.basePrice + (allVideosIncluded ? VIDEO_ADDON_ALL_COURSES : 0);
  }
  const count = new Set(videoCourseIds ?? []).size;
  return terms.basePrice + VIDEO_ADDON_PER_COURSE * count;
}

/** Base price with no add-ons — what the pricing cards lead with. */
export const basePriceOf = (planId: string): number => PLAN_TERMS[planId]?.basePrice ?? 0;

/** How many leaflets the plan lets you pick; 1 when the plan is unknown. */
export const maxCoursesOf = (planId: string): number => PLAN_TERMS[planId]?.maxCourses ?? 1;
